import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/ChatbotStyles.css';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm the Boultbee Vegetation Management assistant. I can answer questions about our services, coverage areas, certifications, and more. How can I help you today?"
};

const MAX_MESSAGES = 20;
const MAX_INPUT_LENGTH = 1000;
const RATE_LIMIT_MS = 2000;

const ChatbotWidget = () => {
  // react-snap bypass: don't render during prerender
  const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent?.includes('ReactSnap');

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasOpened, setHasOpened] = useState(false);
  const [lastSentAt, setLastSentAt] = useState(0);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const toggleChat = useCallback(() => {
    setIsOpen(prev => {
      const opening = !prev;
      if (opening && !hasOpened) {
        setMessages([WELCOME_MESSAGE]);
        setHasOpened(true);
      }
      return opening;
    });
  }, [hasOpened]);

  const userMessageCount = messages.filter(m => m.role === 'user').length;
  const atLimit = userMessageCount >= MAX_MESSAGES;

  const sendMessage = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading || atLimit) return;

    // Rate limit
    const now = Date.now();
    if (now - lastSentAt < RATE_LIMIT_MS) return;
    setLastSentAt(now);

    setError('');
    const userMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      const res = await fetch(process.env.REACT_APP_CHATBOT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updatedMessages
            .filter(m => m.role === 'user' || m.role === 'assistant')
            .map(m => ({ role: m.role, content: m.content }))
        })
      });

      const data = await res.json();

      if (res.ok && data.reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Unable to connect. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, atLimit, lastSentAt, messages]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  if (isPrerender) return null;

  return (
    <>
      {/* Chat bubble */}
      {!isOpen && (
        <button
          className="chatbot-bubble"
          onClick={toggleChat}
          aria-label="Open chat"
          title="Chat with us"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="chatbot-window" role="dialog" aria-label="Chat with Boultbee Vegetation Management">
          {/* Header */}
          <div className="chatbot-header">
            <span className="chatbot-header-title">Boultbee VM Assistant</span>
            <button
              className="chatbot-header-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              &times;
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages" aria-live="polite">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`chatbot-message ${
                  msg.role === 'user' ? 'chatbot-message-user' : 'chatbot-message-assistant'
                }`}
              >
                {msg.content}
              </div>
            ))}

            {isLoading && (
              <div className="chatbot-typing" aria-label="Assistant is typing">
                <span className="chatbot-typing-dot" />
                <span className="chatbot-typing-dot" />
                <span className="chatbot-typing-dot" />
              </div>
            )}

            {error && (
              <div className="chatbot-error">{error}</div>
            )}

            {atLimit && (
              <div className="chatbot-limit">
                You've reached the message limit. Please call us at (250) 492-0296 or use our <a href="/contact" style={{ color: '#046c4e', textDecoration: 'underline' }}>contact form</a> for further assistance.
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="chatbot-input-area">
            <textarea
              ref={inputRef}
              className="chatbot-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value.slice(0, MAX_INPUT_LENGTH))}
              onKeyDown={handleKeyDown}
              placeholder={atLimit ? 'Message limit reached' : 'Type a message...'}
              disabled={atLimit || isLoading}
              rows={1}
              aria-label="Type your message"
            />
            <button
              className="chatbot-send"
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading || atLimit}
              aria-label="Send message"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
